import { useState } from 'react';
import { Pause, Play, Square, Archive, RotateCw, Loader2 } from 'lucide-react';
import { api } from '../lib/api';
import type { SessionStatus } from '../types';

interface SessionControlsProps {
  sessionId: string;
  status: SessionStatus;
  onChange: () => void;
}

export function SessionControls({ sessionId, status, onChange }: SessionControlsProps) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(label);
    setError(null);
    try {
      await fn();
      onChange();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  const isActive = ['initializing', 'ready', 'running'].includes(status);
  const isPaused = status === 'paused';
  const isStopped = status === 'stopped';
  const isArchived = status === 'archived';

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {isActive && (
        <button
          type="button"
          onClick={() => run('pause', () => api.pause(sessionId))}
          disabled={!!busy}
          className="btn"
        >
          {busy === 'pause' ? <Loader2 size={14} className="animate-spin" /> : <Pause size={14} />}
          Pausar
        </button>
      )}

      {isPaused && (
        <button
          type="button"
          onClick={() => run('resume', () => api.resume(sessionId))}
          disabled={!!busy}
          className="btn"
        >
          {busy === 'resume' ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Play size={14} />
          )}
          Retomar
        </button>
      )}

      {(isActive || isPaused) && (
        <button
          type="button"
          onClick={() => run('stop', () => api.stop(sessionId))}
          disabled={!!busy}
          className="btn btn-danger"
        >
          {busy === 'stop' ? <Loader2 size={14} className="animate-spin" /> : <Square size={14} />}
          Parar
        </button>
      )}

      {(isStopped || isArchived) && (
        <button
          type="button"
          onClick={() => run('reactivate', () => api.reactivate(sessionId))}
          disabled={!!busy}
          className="btn"
          title={
            isArchived
              ? 'Re-clona o worktree a partir da branch no git e injeta o histórico anterior como contexto'
              : 'Reativa a sessão criando um novo runner com o histórico anterior como contexto'
          }
        >
          {busy === 'reactivate' ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <RotateCw size={14} />
          )}
          Reativar
        </button>
      )}

      {isStopped && (
        <button
          type="button"
          onClick={() => run('archive', () => api.archive(sessionId))}
          disabled={!!busy}
          className="btn"
          title="Faz git push da branch e remove o worktree local"
        >
          {busy === 'archive' ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Archive size={14} />
          )}
          Arquivar
        </button>
      )}

      {error && <span className="text-xs text-red-600">{error}</span>}
    </div>
  );
}
